import React, { useEffect, useRef, useState } from 'react';
import {
  Plus,
  Trash2,
  Save,
  ArrowLeft,
  Sparkles,
  FileUp,
  Loader2,
  AlertTriangle,
  GripVertical,
  Clock,
  Zap,
  Check,
  Wand2,
  Info,
  Globe,
  FileText,
  ExternalLink,
  Link2,
  Lock,
} from 'lucide-react';

import {
  MAX_QUESTIONS_PER_QUIZ,
  normalizeVisibility,
  type QuestionAuthor,
  type QuestionType,
  type QuizVisibility,
} from '../../shared/protocol';
import { api, type QuizSummary, type AiStatus } from '../lib/api';
import { ANSWER_SHAPES } from '../brand';
import { AnswerShapeIcon } from './questions/AnswerShapeIcon';
import { EmojiPicker } from './EmojiPicker';
import { NumberPadField } from './NumberPadField';
import { LANGUAGES, DEFAULT_LANGUAGE } from '../../shared/languages';

const VISIBILITY_OPTIONS: { value: QuizVisibility; label: string; hint: string; Icon: typeof Globe }[] = [
  { value: 'public', label: 'Pública', hint: 'Sale en el catálogo y la búsqueda.', Icon: Globe },
  { value: 'unlisted', label: 'No listada', hint: 'Solo quien tenga el enlace.', Icon: Link2 },
  { value: 'private', label: 'Privada', hint: 'Solo tú puedes verla y jugarla.', Icon: Lock },
];

/** De dónde saca la IA el contenido: lo que investigue en la web, o un PDF. */
export type ModoIa = 'tema' | 'pdf';

interface Props {
  quizId: string | null;
  onSaved: (quiz: QuizSummary) => void;
  onCancel: () => void;
  /** Abre con el panel de generación desplegado y en esa pestaña. Lo decide el selector previo. */
  iniciarConIa?: ModoIa | false;
}

/**
 * Tipos que la IA puede generar, en el orden en que se ofrecen.
 *
 * Es la misma lista que acepta el servidor (`TIPOS_PREGUNTA` en server/ai.ts).
 */
const TIPOS_GENERABLES: readonly QuestionType[] = [
  'multiple_choice',
  'true_false',
  'multi_select',
  'fill_blank',
  'match_pairs',
];

const TYPE_LABELS: Record<QuestionType, string> = {
  multiple_choice: 'Opción múltiple',
  true_false: 'Verdadero / Falso',
  multi_select: 'Varias correctas',
  fill_blank: 'Respuesta escrita',
  match_pairs: 'Emparejar',
};

const TIME_OPTIONS = [10, 15, 20, 30, 45, 60, 90, 120];

const uid = () =>
  globalThis.crypto?.randomUUID?.() ?? `id-${Date.now()}-${Math.random().toString(36).slice(2)}`;

function blankQuestion(type: QuestionType): QuestionAuthor {
  const base: QuestionAuthor = {
    id: uid(),
    type,
    title: '',
    timeLimit: 20,
    pointsMultiplier: 1,
  };

  if (type === 'true_false') {
    base.options = [
      { id: uid(), text: 'Verdadero', isCorrect: true },
      { id: uid(), text: 'Falso', isCorrect: false },
    ];
  } else if (type === 'multiple_choice') {
    base.options = Array.from({ length: 4 }, (_, i) => ({
      id: uid(),
      text: '',
      isCorrect: i === 0,
    }));
  } else if (type === 'multi_select') {
    base.options = Array.from({ length: 4 }, (_, i) => ({
      id: uid(),
      text: '',
      isCorrect: i < 2,
    }));
  } else if (type === 'fill_blank') {
    base.acceptedAnswers = [''];
  } else if (type === 'match_pairs') {
    base.pairs = Array.from({ length: 3 }, () => ({ id: uid(), left: '', right: '' }));
  }

  return base;
}

export const QuizCreator: React.FC<Props> = ({ quizId, onSaved, onCancel, iniciarConIa }) => {
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [category, setCategory] = useState('General');
  const [language, setLanguage] = useState<string>(DEFAULT_LANGUAGE);
  const [coverEmoji, setCoverEmoji] = useState('🦌');
  const [visibility, setVisibility] = useState<QuizVisibility>('public');
  const [questions, setQuestions] = useState<QuestionAuthor[]>([]);
  const [activeIndex, setActiveIndex] = useState(0);

  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [ai, setAi] = useState<AiStatus | null>(null);
  const [showAiPanel, setShowAiPanel] = useState(Boolean(iniciarConIa));
  const [resultadoIa, setResultadoIa] = useState<ResultadoIa | null>(null);

  /* ---------------- carga ---------------- */

  useEffect(() => {
    void (async () => {
      try {
        setAi(await api.get<AiStatus>('/ai/status'));
      } catch {
        setAi({ configured: false, provider: null, model: null, usingOwnKey: false });
      }
    })();
  }, []);

  useEffect(() => {
    if (!quizId) {
      setQuestions([blankQuestion('multiple_choice')]);
      return;
    }
    void (async () => {
      try {
        const data = await api.get<{ quiz: QuizSummary & { questions: QuestionAuthor[] } }>(
          `/quizzes/${quizId}`,
        );
        setTitle(data.quiz.title);
        setDescription(data.quiz.description);
        setCategory(data.quiz.category);
        setLanguage(data.quiz.language ?? DEFAULT_LANGUAGE);
        setCoverEmoji(data.quiz.coverEmoji);
        setVisibility(normalizeVisibility(data.quiz.visibility) ?? 'public');
        setQuestions(data.quiz.questions);
      } catch {
        setError('No se pudo cargar el quiz para editar.');
      }
    })();
  }, [quizId]);

  /* ---------------- manipulación ---------------- */

  const updateQuestion = (index: number, patch: Partial<QuestionAuthor>) =>
    setQuestions((prev) => prev.map((q, i) => (i === index ? { ...q, ...patch } : q)));

  const changeType = (index: number, type: QuestionType) =>
    setQuestions((prev) =>
      prev.map((q, i) => (i === index ? { ...blankQuestion(type), title: q.title, timeLimit: q.timeLimit } : q)),
    );

  const atLimit = questions.length >= MAX_QUESTIONS_PER_QUIZ;

  const addQuestion = () => {
    if (atLimit) return;
    setQuestions((prev) => [...prev, blankQuestion('multiple_choice')]);
    setActiveIndex(questions.length);
  };

  const removeQuestion = (index: number) => {
    setQuestions((prev) => prev.filter((_, i) => i !== index));
    setActiveIndex((prev) => Math.max(0, Math.min(prev, questions.length - 2)));
  };

  /* ---------------- guardar ---------------- */

  const save = async () => {
    setError('');
    if (!title.trim()) return setError('Ponle un título al quiz.');
    if (questions.length === 0) return setError('Añade al menos una pregunta.');

    setSaving(true);
    try {
      const data = await api.post<{ quiz: QuizSummary }>('/quizzes', {
        id: quizId ?? undefined,
        title,
        description,
        category,
        language,
        coverEmoji,
        visibility,
        questions,
      });
      onSaved(data.quiz);
    } catch (err) {
      // El servidor valida pregunta por pregunta y dice cuál falla.
      setError(err instanceof Error ? err.message : 'No se pudo guardar.');
    } finally {
      setSaving(false);
    }
  };

  const active = questions[activeIndex];

  return (
    <div className="max-w-6xl mx-auto px-4 py-6 space-y-5">
      {/* Barra superior */}
      <div className="flex items-center gap-3 flex-wrap">
        <button
          onClick={onCancel}
          className="p-2 rounded-lg text-ink-400 hover:text-ink-100 hover:bg-ink-800 transition-colors"
          aria-label="Volver"
        >
          <ArrowLeft className="w-5 h-5" />
        </button>
        <h1 className="text-xl font-bold flex-1">
          {quizId ? 'Editar quiz' : 'Nuevo quiz'}
        </h1>

        <button
          onClick={() => setShowAiPanel((v) => !v)}
          className={`flex items-center gap-2 px-4 py-2.5 rounded-xl font-semibold text-sm border transition-colors ${
            showAiPanel
              ? 'bg-sky-600 border-sky-500 text-white'
              : 'bg-ink-850 border-ink-700 text-ink-200 hover:border-ink-600'
          }`}
        >
          <Wand2 className="w-4 h-4" />
          Generar con IA
        </button>

        <button
          onClick={save}
          disabled={saving}
          className="flex items-center gap-2 px-5 py-2.5 rounded-xl font-bold bg-ember-500 hover:bg-ember-400 text-on-accent disabled:bg-ink-800 disabled:text-ink-500 transition-colors"
        >
          {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
          Guardar
        </button>
      </div>

      {showAiPanel && (
        <AiPanel
          status={ai}
          language={language}
          modoInicial={iniciarConIa || 'tema'}
          onGenerated={(quiz, extra) => {
            setResultadoIa(extra);
            setTitle((t) => t || quiz.title);
            setDescription((d) => d || quiz.description);
            setCategory(quiz.category);
            setCoverEmoji(quiz.coverEmoji);
            // Se añaden a lo que ya había en lugar de reemplazarlo: nadie quiere
            // perder diez preguntas escritas a mano por pulsar un botón.
            // Lo que no quepa bajo el tope se descarta aquí, antes de que el
            // servidor rechace el quiz entero al guardar.
            setQuestions((prev) =>
              [...prev.filter((q) => q.title.trim()), ...quiz.questions].slice(
                0,
                MAX_QUESTIONS_PER_QUIZ,
              ),
            );
            setShowAiPanel(false);
          }}
        />
      )}

      {resultadoIa && (resultadoIa.aviso || resultadoIa.fuentes?.length) ? (
        <div className="surface p-4 space-y-2 text-sm">
          {resultadoIa.aviso && (
            <p className="flex items-start gap-2 text-ember-300">
              <Info className="w-4 h-4 shrink-0 mt-0.5" />
              {resultadoIa.aviso}
            </p>
          )}
          {resultadoIa.fuentes && resultadoIa.fuentes.length > 0 && (
            <details>
              <summary className="cursor-pointer text-ink-300 font-medium">
                Fuentes que consultó la IA ({resultadoIa.fuentes.length})
              </summary>
              <ul className="mt-2 space-y-1">
                {resultadoIa.fuentes.map((f) => (
                  <li key={f.url}>
                    <a
                      href={f.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-1.5 text-sky-400 hover:text-sky-300"
                    >
                      <ExternalLink className="w-3.5 h-3.5 shrink-0" />
                      {f.titulo}
                    </a>
                  </li>
                ))}
              </ul>
            </details>
          )}
        </div>
      ) : null}

      {error && (
        <p className="flex items-start gap-2 p-3 rounded-xl bg-danger-500/10 border border-danger-500 text-danger-400 text-sm">
          <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5" />
          {error}
        </p>
      )}

      {/* Ficha */}
      <div className="surface p-5 space-y-4">
        <div className="flex gap-4 items-start">
          <div className="space-y-1.5">
            <span className="text-xs text-ink-400">Portada</span>
            <EmojiPicker value={coverEmoji} onChange={setCoverEmoji} />
          </div>

          <div className="flex-1 space-y-3">
            <input
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="Título del quiz"
              maxLength={120}
              className="w-full px-4 py-3 rounded-xl bg-ink-850 border border-ink-700 text-ink-50 text-lg font-semibold placeholder:text-ink-600 outline-none focus:border-sky-600 transition-colors"
            />
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Descripción breve (opcional)"
              rows={2}
              maxLength={400}
              className="w-full px-4 py-3 rounded-xl bg-ink-850 border border-ink-700 text-ink-100 placeholder:text-ink-600 outline-none focus:border-sky-600 transition-colors resize-none"
            />
            {/* Categoría e idioma son lo que clasifica la trivia en el
                catálogo: la categoría la escribe libremente quien la crea, el
                idioma se elige de una lista para que el filtro pueda agrupar. */}
            <div className="grid sm:grid-cols-2 gap-3">
              <label className="block">
                <span className="block text-xs font-medium text-ink-300 mb-1.5">Categoría</span>
                <input
                  value={category}
                  onChange={(e) => setCategory(e.target.value)}
                  placeholder="Historia, Biología, Cultura pop…"
                  maxLength={60}
                  className="w-full h-11 px-3.5 rounded-lg bg-ink-850 border border-ink-700 text-ink-100 text-sm placeholder:text-ink-600 outline-none transition-colors focus:border-sky-500 focus:ring-2 focus:ring-sky-500/25"
                />
              </label>
              <label className="block">
                <span className="block text-xs font-medium text-ink-300 mb-1.5">Idioma</span>
                <select
                  value={language}
                  onChange={(e) => setLanguage(e.target.value)}
                  className="w-full h-11 px-3.5 rounded-lg bg-ink-850 border border-ink-700 text-ink-100 text-sm outline-none transition-colors focus:border-sky-500 focus:ring-2 focus:ring-sky-500/25"
                >
                  {LANGUAGES.map((l) => (
                    <option key={l.code} value={l.code}>
                      {l.name}
                    </option>
                  ))}
                </select>
              </label>
            </div>
            <fieldset>
              <legend className="block text-xs font-medium text-ink-300 mb-1.5">Visibilidad</legend>
              <div role="radiogroup" className="grid sm:grid-cols-3 gap-2">
                {VISIBILITY_OPTIONS.map(({ value, label, hint, Icon }) => {
                  const on = visibility === value;
                  return (
                    <button
                      key={value}
                      type="button"
                      role="radio"
                      aria-checked={on}
                      onClick={() => setVisibility(value)}
                      className={`flex items-start gap-2.5 px-3.5 py-2.5 rounded-lg border text-left transition-colors ${
                        on
                          ? 'bg-sky-600/15 border-sky-500'
                          : 'bg-ink-850 border-ink-700 hover:border-ink-600'
                      }`}
                    >
                      <Icon className={`w-4 h-4 mt-0.5 shrink-0 ${on ? 'text-sky-400' : 'text-ink-400'}`} />
                      <span className="min-w-0">
                        <span className="block text-sm font-semibold text-ink-100">{label}</span>
                        <span className="block text-xs text-ink-500">{hint}</span>
                      </span>
                    </button>
                  );
                })}
              </div>
            </fieldset>
          </div>
        </div>
      </div>

      <div className="grid lg:grid-cols-[260px_1fr] gap-5">
        {/* Lista */}
        <aside className="space-y-2">
          <div className="flex items-center justify-between">
            <h2 className="text-sm font-semibold text-ink-300">
              {questions.length} {questions.length === 1 ? 'pregunta' : 'preguntas'}
              <span className="text-ink-500 font-normal"> / {MAX_QUESTIONS_PER_QUIZ} máx.</span>
            </h2>
            <button
              onClick={addQuestion}
              disabled={atLimit}
              className="p-1.5 rounded-lg text-sky-400 hover:bg-sky-500/10 transition-colors disabled:text-ink-600 disabled:hover:bg-transparent disabled:cursor-not-allowed"
              aria-label="Añadir pregunta"
            >
              <Plus className="w-5 h-5" />
            </button>
          </div>

          <ol className="space-y-1.5 max-h-[60vh] overflow-y-auto pr-1">
            {questions.map((q, i) => (
              <li key={q.id}>
                <button
                  onClick={() => setActiveIndex(i)}
                  className={`w-full flex items-center gap-2 px-3 py-2.5 rounded-lg text-left transition-colors ${
                    i === activeIndex
                      ? 'bg-sky-600/20 border border-sky-600'
                      : 'bg-ink-850 border border-ink-800 hover:border-ink-700'
                  }`}
                >
                  <GripVertical className="w-4 h-4 text-ink-600 shrink-0" />
                  <span className="w-5 text-ink-500 text-xs font-mono shrink-0">{i + 1}</span>
                  <span className="flex-1 min-w-0">
                    <span className="block text-sm text-ink-100 truncate">
                      {q.title || <span className="text-ink-500 italic">Sin enunciado</span>}
                    </span>
                    <span className="block text-[11px] text-ink-500">{TYPE_LABELS[q.type]}</span>
                  </span>
                </button>
              </li>
            ))}
          </ol>

          <button
            onClick={addQuestion}
            disabled={atLimit}
            className="w-full flex items-center justify-center gap-2 py-2.5 rounded-lg border border-dashed border-ink-700 text-ink-400 hover:border-sky-600 hover:text-sky-400 transition-colors text-sm disabled:hover:border-ink-700 disabled:hover:text-ink-400 disabled:opacity-60 disabled:cursor-not-allowed"
          >
            <Plus className="w-4 h-4" />
            {atLimit ? `Máximo ${MAX_QUESTIONS_PER_QUIZ} preguntas` : 'Añadir pregunta'}
          </button>
        </aside>

        {/* Editor */}
        {active ? (
          <QuestionEditor
            key={active.id}
            question={active}
            index={activeIndex}
            canDelete={questions.length > 1}
            onChange={(patch) => updateQuestion(activeIndex, patch)}
            onChangeType={(type) => changeType(activeIndex, type)}
            onDelete={() => removeQuestion(activeIndex)}
          />
        ) : (
          <div className="surface p-12 text-center text-ink-500">
            Añade una pregunta para empezar.
          </div>
        )}
      </div>
    </div>
  );
};

/* ================================================================
 * Panel de IA
 * ================================================================ */

/** Tope de subida. Tiene que coincidir con `express.raw` en /ai/from-pdf y con nginx. */
const MAX_PDF_MB = 25;

type QuizGenerado = {
  title: string;
  description: string;
  category: string;
  coverEmoji: string;
  questions: QuestionAuthor[];
};

export interface ResultadoIa {
  /** Páginas que la IA consultó al investigar el tema. */
  fuentes?: { titulo: string; url: string }[];
  /** Algo que conviene saber de lo generado, como un PDF recortado. */
  aviso?: string;
}

const AiPanel: React.FC<{
  status: AiStatus | null;
  /** El idioma elegido en el editor; la IA escribe en él. */
  language: string;
  modoInicial: ModoIa;
  onGenerated: (quiz: QuizGenerado, extra: ResultadoIa) => void;
}> = ({ status, language, modoInicial, onGenerated }) => {
  const [modo, setModo] = useState<ModoIa>(modoInicial);
  const [topic, setTopic] = useState('');
  const [pdf, setPdf] = useState<File | null>(null);
  const [arrastrando, setArrastrando] = useState(false);
  // El número se teclea en la emergente de NumberPadField, que ya lo devuelve
  // dentro del rango.
  const [count, setCount] = useState(6);
  // Todos permitidos de entrada: quien no quiera alguno lo desmarca.
  const [tipos, setTipos] = useState<QuestionType[]>(() => [...TIPOS_GENERABLES]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const fileRef = useRef<HTMLInputElement>(null);
  const errorRef = useRef<HTMLParagraphElement>(null);

  // El error sale al final del panel: si no se trae a la vista, parece que el
  // botón no hizo nada.
  useEffect(() => {
    if (error) errorRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
  }, [error]);

  if (status && !status.configured) {
    return (
      <div className="surface p-5 flex items-start gap-3">
        <Info className="w-5 h-5 text-ink-500 shrink-0 mt-0.5" />
        <div className="text-sm text-ink-300 space-y-1.5">
          <p className="font-medium text-ink-100">Te falta tu clave de IA.</p>
          <p>
            La generación la pagas con tu propia clave gratuita de Google AI Studio. Ve a tu
            perfil, pulsa <strong className="text-ink-100">Editar perfil</strong> y abre la
            pestaña <strong className="text-ink-100">IA</strong>: ahí está la guía paso a paso.
          </p>
          <p className="text-ink-500 text-[0.8125rem]">
            Mientras tanto puedes escribir las preguntas a mano, que no necesita nada.
          </p>
        </div>
      </div>
    );
  }

  /** Valida el archivo al elegirlo, no al generar: el error sale donde se está mirando. */
  const elegirPdf = (file: File | undefined) => {
    setError('');
    if (!file) return;
    const esPdf = file.type === 'application/pdf' || /\.pdf$/i.test(file.name);
    if (!esPdf) return setError('Ese archivo no es un PDF.');
    if (file.size > MAX_PDF_MB * 1024 * 1024) {
      return setError(
        `El PDF pesa ${(file.size / 1024 / 1024).toFixed(1)} MB; el máximo es ${MAX_PDF_MB} MB.`,
      );
    }
    setPdf(file);
  };

  const fromTopic = async () => {
    if (!topic.trim()) return;
    setBusy(true);
    setError('');
    try {
      const data = await api.post<{ quiz: QuizGenerado & { fuentes?: ResultadoIa['fuentes'] } }>(
        '/ai/from-topic',
        { topic, count, types: tipos, language },
      );
      onGenerated(data.quiz, { fuentes: data.quiz.fuentes });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Falló la generación.');
    } finally {
      setBusy(false);
    }
  };

  const fromPdf = async () => {
    if (!pdf) return;
    setBusy(true);
    setError('');
    try {
      const data = await api.postPdf<{
        quiz: QuizGenerado;
        source: { pageCount: number; pagesWithText: number; truncated: boolean };
      }>(
        `/ai/from-pdf?count=${count}&name=${encodeURIComponent(pdf.name)}` +
          `&types=${encodeURIComponent(tipos.join(','))}&language=${language}`,
        pdf,
      );
      onGenerated(data.quiz, {
        aviso: data.source.truncated
          ? `El documento era largo (${data.source.pageCount} páginas): las preguntas salen de la primera parte.`
          : undefined,
      });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'No se pudo procesar el PDF.');
    } finally {
      setBusy(false);
    }
  };

  const pestana = (valor: ModoIa, icono: React.ReactNode, texto: string) => (
    <button
      type="button"
      role="tab"
      aria-selected={modo === valor}
      disabled={busy}
      onClick={() => {
        setModo(valor);
        setError('');
      }}
      className={`flex-1 flex items-center justify-center gap-2 px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
        modo === valor ? 'bg-ink-700 text-ink-50' : 'text-ink-400 hover:text-ink-200'
      }`}
    >
      {icono}
      {texto}
    </button>
  );

  return (
    <div className="surface p-5 space-y-4">
      <div className="flex items-center gap-2">
        <Sparkles className="w-4 h-4 text-sky-400" />
        <h2 className="font-semibold">Generar preguntas</h2>
        {status?.model && (
          <span className="chip border-ink-700 bg-ink-850 text-ink-400 ml-auto">
            {status.provider === 'openai' ? 'Modelo local' : 'Gemini'} · {status.model}
          </span>
        )}
      </div>

      <div role="tablist" className="flex gap-1 p-1 rounded-xl bg-ink-850 border border-ink-700">
        {pestana('tema', <Globe className="w-4 h-4" />, 'Investigar un tema')}
        {pestana('pdf', <FileText className="w-4 h-4" />, 'Desde un PDF')}
      </div>

      {modo === 'tema' ? (
        <div className="space-y-2">
          <label className="text-sm font-medium text-ink-300" htmlFor="ai-topic">
            ¿Sobre qué tema?
          </label>
          <input
            id="ai-topic"
            value={topic}
            onChange={(e) => setTopic(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && !busy && fromTopic()}
            placeholder="La Revolución Francesa, fotosíntesis, el Mundial 2026…"
            disabled={busy}
            className="w-full px-4 py-3 rounded-xl bg-ink-850 border border-ink-700 text-ink-100 placeholder:text-ink-600 outline-none focus:border-sky-600 transition-colors"
          />
          <p className="text-ink-500 text-xs">
            {status?.provider === 'openai'
              ? 'Tu modelo local no puede buscar en la web: usará lo que ya sabe.'
              : 'La IA busca en Google, contrasta los datos y te enseña las fuentes que consultó.'}
          </p>
        </div>
      ) : (
        <div className="space-y-2">
          <span className="text-sm font-medium text-ink-300">Tu documento</span>
          <input
            ref={fileRef}
            type="file"
            accept="application/pdf,.pdf"
            className="sr-only"
            onChange={(e) => {
              elegirPdf(e.target.files?.[0]);
              e.target.value = '';
            }}
          />
          <button
            type="button"
            onClick={() => fileRef.current?.click()}
            onDragOver={(e) => {
              e.preventDefault();
              setArrastrando(true);
            }}
            onDragLeave={() => setArrastrando(false)}
            onDrop={(e) => {
              e.preventDefault();
              setArrastrando(false);
              if (!busy) elegirPdf(e.dataTransfer.files?.[0]);
            }}
            disabled={busy}
            className={`w-full flex items-center justify-center gap-3 px-4 py-6 rounded-xl border-2 border-dashed transition-colors disabled:opacity-60 ${
              arrastrando
                ? 'border-sky-500 bg-sky-500/10 text-sky-300'
                : pdf
                  ? 'border-sky-600/60 bg-sky-500/5 text-ink-100'
                  : 'border-ink-700 hover:border-sky-600 text-ink-300 hover:text-sky-400'
            }`}
          >
            {pdf ? (
              <FileText className="w-5 h-5 shrink-0 text-sky-400" />
            ) : (
              <FileUp className="w-5 h-5 shrink-0" />
            )}
            {pdf ? (
              <span className="min-w-0 text-left">
                <span className="block font-medium truncate">{pdf.name}</span>
                <span className="block text-xs text-ink-400">
                  {(pdf.size / 1024 / 1024).toFixed(1)} MB · pulsa para cambiarlo
                </span>
              </span>
            ) : (
              <span>Elige o arrastra un PDF (hasta {MAX_PDF_MB} MB)</span>
            )}
          </button>
          <p className="text-ink-500 text-xs">
            Las preguntas saldrán solo del contenido del documento. Un PDF escaneado sin texto
            seleccionable no sirve: necesitaría OCR antes.
          </p>
        </div>
      )}

      <div className="flex items-center gap-3 flex-wrap">
        <span className="text-sm text-ink-400">Cuántas preguntas</span>
        <NumberPadField
          value={count}
          onChange={setCount}
          min={3}
          max={30}
          label="Cuántas preguntas"
          unit="preguntas"
        />
      </div>

      {/* Qué tipos puede usar la IA */}
      <fieldset className="space-y-2">
        <legend className="text-sm font-medium text-ink-300">
          Tipos de pregunta que puede generar
        </legend>
        <div className="flex flex-wrap gap-2">
          {TIPOS_GENERABLES.map((tipo) => {
            const activo = tipos.includes(tipo);
            // No se permite dejar la lista vacía: sin ningún tipo no hay nada
            // que generar, y el servidor acabaría permitiéndolos todos.
            const ultimo = activo && tipos.length === 1;
            return (
              <button
                key={tipo}
                type="button"
                role="switch"
                aria-checked={activo}
                disabled={ultimo}
                title={ultimo ? 'Deja al menos un tipo' : undefined}
                onClick={() =>
                  setTipos((prev) =>
                    prev.includes(tipo) ? prev.filter((t) => t !== tipo) : [...prev, tipo],
                  )
                }
                className={`flex items-center gap-1.5 px-3 py-2 rounded-lg text-[0.8125rem] font-medium border transition-colors ${
                  activo
                    ? 'border-sky-500/40 bg-sky-500/10 text-ink-100'
                    : 'border-ink-700 bg-ink-850 text-ink-500 hover:text-ink-300'
                } ${ultimo ? 'cursor-not-allowed' : ''}`}
              >
                {activo ? <Check className="w-3.5 h-3.5 text-sky-400" /> : <Plus className="w-3.5 h-3.5" />}
                {TYPE_LABELS[tipo]}
              </button>
            );
          })}
        </div>
        <p className="text-[0.6875rem] text-ink-500">
          Desmarca los que no quieras. Si la IA cuela alguno de más, se descarta.
        </p>
      </fieldset>

      <button
        onClick={modo === 'tema' ? fromTopic : fromPdf}
        disabled={busy || (modo === 'tema' ? !topic.trim() : !pdf)}
        className="w-full flex items-center justify-center gap-2 py-3 rounded-xl font-semibold bg-sky-600 hover:bg-sky-500 text-white disabled:bg-ink-800 disabled:text-ink-500 transition-colors"
      >
        {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />}
        {busy
          ? modo === 'tema'
            ? 'Investigando y escribiendo preguntas…'
            : 'Leyendo el documento y escribiendo preguntas…'
          : modo === 'tema'
            ? 'Investigar y generar'
            : 'Generar desde el PDF'}
      </button>
      {busy && (
        <p className="text-ink-500 text-xs text-center">
          Suele tardar entre 15 segundos y un minuto. No cierres esta pantalla.
        </p>
      )}

      {error && (
        <p ref={errorRef} role="alert" className="flex items-start gap-2 text-danger-400 text-sm">
          <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5" />
          {error}
        </p>
      )}
      <p className="text-ink-500 text-xs">
        Revisa siempre lo generado antes de jugar: la IA se equivoca, y un dato falso proyectado en
        un aula es peor que no tener quiz.
      </p>
    </div>
  );
};

/* ================================================================
 * Editor de pregunta
 * ================================================================ */

const QuestionEditor: React.FC<{
  question: QuestionAuthor;
  index: number;
  canDelete: boolean;
  onChange: (patch: Partial<QuestionAuthor>) => void;
  onChangeType: (type: QuestionType) => void;
  onDelete: () => void;
}> = ({ question, index, canDelete, onChange, onChangeType, onDelete }) => {
  const setOption = (id: string, patch: Partial<{ text: string; isCorrect: boolean }>) =>
    onChange({
      options: question.options?.map((o) => (o.id === id ? { ...o, ...patch } : o)),
    });

  const selectSingle = (id: string) =>
    onChange({
      options: question.options?.map((o) => ({ ...o, isCorrect: o.id === id })),
    });

  return (
    <div className="surface p-5 space-y-5">
      {/* Cabecera */}
      <div className="flex items-center gap-2 flex-wrap">
        <span className="chip border-ink-700 bg-ink-850 text-ink-300">Pregunta {index + 1}</span>
        <select
          value={question.type}
          onChange={(e) => onChangeType(e.target.value as QuestionType)}
          className="px-3 py-2 rounded-lg bg-ink-850 border border-ink-700 text-ink-100 text-sm outline-none focus:border-sky-600"
          aria-label="Tipo de pregunta"
        >
          {Object.entries(TYPE_LABELS).map(([value, label]) => (
            <option key={value} value={value}>
              {label}
            </option>
          ))}
        </select>

        <label className="flex items-center gap-1.5 text-sm">
          <Clock className="w-4 h-4 text-ink-500" />
          <select
            value={question.timeLimit}
            onChange={(e) => onChange({ timeLimit: Number(e.target.value) })}
            className="px-2 py-2 rounded-lg bg-ink-850 border border-ink-700 text-ink-100 text-sm outline-none focus:border-sky-600"
            aria-label="Tiempo límite"
          >
            {TIME_OPTIONS.map((t) => (
              <option key={t} value={t}>
                {t}s
              </option>
            ))}
          </select>
        </label>

        <label className="flex items-center gap-1.5 text-sm">
          <Zap className="w-4 h-4 text-ember-400" />
          <select
            value={question.pointsMultiplier}
            onChange={(e) => onChange({ pointsMultiplier: Number(e.target.value) as 0 | 1 | 2 })}
            className="px-2 py-2 rounded-lg bg-ink-850 border border-ink-700 text-ink-100 text-sm outline-none focus:border-sky-600"
            aria-label="Multiplicador de puntos"
          >
            <option value={0}>Sin puntos</option>
            <option value={1}>Puntos normales</option>
            <option value={2}>Puntos dobles</option>
          </select>
        </label>

        {canDelete && (
          <button
            onClick={onDelete}
            className="ml-auto p-2 rounded-lg text-ink-500 hover:text-danger-400 hover:bg-danger-500/10 transition-colors"
            aria-label="Eliminar pregunta"
          >
            <Trash2 className="w-4 h-4" />
          </button>
        )}
      </div>

      {/* Enunciado */}
      <textarea
        value={question.title}
        onChange={(e) => onChange({ title: e.target.value })}
        placeholder="Escribe la pregunta…"
        rows={2}
        maxLength={400}
        className="w-full px-4 py-3 rounded-xl bg-ink-850 border border-ink-700 text-ink-50 text-lg placeholder:text-ink-600 outline-none focus:border-sky-600 transition-colors resize-none"
      />

      {/* Opciones */}
      {(question.type === 'multiple_choice' || question.type === 'multi_select') && (
        <div className="space-y-2">
          <p className="text-sm text-ink-400">
            {question.type === 'multiple_choice'
              ? 'Marca la única opción correcta'
              : 'Marca todas las correctas (al menos dos, no todas)'}
          </p>
          {question.options?.map((opt, i) => {
            const shape = ANSWER_SHAPES[i % ANSWER_SHAPES.length];
            return (
              <div key={opt.id} className="flex items-center gap-2">
                <span className="text-ink-400" style={{ color: shape.hex }}>
                  <AnswerShapeIcon shape={shape.id} className="w-5 h-5" />
                </span>
                <input
                  value={opt.text}
                  onChange={(e) => setOption(opt.id, { text: e.target.value })}
                  placeholder={`Opción ${i + 1}`}
                  maxLength={200}
                  className="flex-1 px-4 py-2.5 rounded-lg bg-ink-850 border border-ink-700 text-ink-100 placeholder:text-ink-600 outline-none focus:border-sky-600 transition-colors"
                />
                <button
                  onClick={() =>
                    question.type === 'multiple_choice'
                      ? selectSingle(opt.id)
                      : setOption(opt.id, { isCorrect: !opt.isCorrect })
                  }
                  aria-pressed={opt.isCorrect}
                  aria-label={opt.isCorrect ? 'Correcta' : 'Marcar como correcta'}
                  className={`w-10 h-10 rounded-lg shrink-0 flex items-center justify-center border-2 transition-colors ${
                    opt.isCorrect
                      ? 'bg-success-500 border-success-400 text-white'
                      : 'bg-ink-850 border-ink-700 text-ink-600 hover:border-ink-600'
                  }`}
                >
                  <Check className="w-5 h-5" strokeWidth={3} />
                </button>
              </div>
            );
          })}
        </div>
      )}

      {question.type === 'true_false' && (
        <div className="flex gap-2">
          {question.options?.map((opt) => (
            <button
              key={opt.id}
              onClick={() => selectSingle(opt.id)}
              aria-pressed={opt.isCorrect}
              className={`flex-1 py-4 rounded-xl border-2 font-semibold transition-colors ${
                opt.isCorrect
                  ? 'bg-success-500 border-success-400 text-white'
                  : 'bg-ink-850 border-ink-700 text-ink-300 hover:border-ink-600'
              }`}
            >
              {opt.text}
            </button>
          ))}
        </div>
      )}

      {question.type === 'fill_blank' && (
        <div className="space-y-2">
          <p className="text-sm text-ink-400">
            Respuestas aceptadas. No hace falta añadir variantes de tildes ni mayúsculas: eso se
            ignora al corregir.
          </p>
          {question.acceptedAnswers?.map((answer, i) => (
            <div key={i} className="flex gap-2">
              <input
                value={answer}
                onChange={(e) =>
                  onChange({
                    acceptedAnswers: question.acceptedAnswers?.map((a, j) =>
                      j === i ? e.target.value : a,
                    ),
                  })
                }
                placeholder={i === 0 ? 'Respuesta principal' : 'Variante aceptada'}
                className="flex-1 px-4 py-2.5 rounded-lg bg-ink-850 border border-ink-700 text-ink-100 placeholder:text-ink-600 outline-none focus:border-sky-600 transition-colors"
              />
              {(question.acceptedAnswers?.length ?? 0) > 1 && (
                <button
                  onClick={() =>
                    onChange({
                      acceptedAnswers: question.acceptedAnswers?.filter((_, j) => j !== i),
                    })
                  }
                  className="p-2.5 rounded-lg text-ink-500 hover:text-danger-400 transition-colors"
                  aria-label="Quitar variante"
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              )}
            </div>
          ))}
          <button
            onClick={() =>
              onChange({ acceptedAnswers: [...(question.acceptedAnswers ?? []), ''] })
            }
            className="text-sky-400 hover:text-sky-300 text-sm flex items-center gap-1.5"
          >
            <Plus className="w-4 h-4" />
            Añadir variante
          </button>
        </div>
      )}

      {question.type === 'match_pairs' && (
        <div className="space-y-2">
          <p className="text-sm text-ink-400">
            Parejas. La columna derecha se baraja sola al jugar.
          </p>
          {question.pairs?.map((pair, i) => (
            <div key={pair.id} className="flex gap-2 items-center">
              <input
                value={pair.left}
                onChange={(e) =>
                  onChange({
                    pairs: question.pairs?.map((p, j) =>
                      j === i ? { ...p, left: e.target.value } : p,
                    ),
                  })
                }
                placeholder="Elemento"
                className="flex-1 px-4 py-2.5 rounded-lg bg-ink-850 border border-ink-700 text-ink-100 placeholder:text-ink-600 outline-none focus:border-sky-600 transition-colors"
              />
              <span className="text-ink-600" aria-hidden="true">
                ↔
              </span>
              <input
                value={pair.right}
                onChange={(e) =>
                  onChange({
                    pairs: question.pairs?.map((p, j) =>
                      j === i ? { ...p, right: e.target.value } : p,
                    ),
                  })
                }
                placeholder="Su pareja"
                className="flex-1 px-4 py-2.5 rounded-lg bg-ink-850 border border-ink-700 text-ink-100 placeholder:text-ink-600 outline-none focus:border-sky-600 transition-colors"
              />
              {(question.pairs?.length ?? 0) > 2 && (
                <button
                  onClick={() =>
                    onChange({ pairs: question.pairs?.filter((_, j) => j !== i) })
                  }
                  className="p-2.5 rounded-lg text-ink-500 hover:text-danger-400 transition-colors"
                  aria-label="Quitar pareja"
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              )}
            </div>
          ))}
          {(question.pairs?.length ?? 0) < 8 && (
            <button
              onClick={() =>
                onChange({ pairs: [...(question.pairs ?? []), { id: uid(), left: '', right: '' }] })
              }
              className="text-sky-400 hover:text-sky-300 text-sm flex items-center gap-1.5"
            >
              <Plus className="w-4 h-4" />
              Añadir pareja
            </button>
          )}
        </div>
      )}

      {/* Explicación */}
      <div className="space-y-1.5">
        <label className="text-sm text-ink-400" htmlFor={`explain-${question.id}`}>
          Explicación (se muestra al revelar la respuesta)
        </label>
        <textarea
          id={`explain-${question.id}`}
          value={question.explanation ?? ''}
          onChange={(e) => onChange({ explanation: e.target.value })}
          rows={2}
          maxLength={600}
          placeholder="Por qué esa es la respuesta…"
          className="w-full px-4 py-2.5 rounded-lg bg-ink-850 border border-ink-700 text-ink-100 placeholder:text-ink-600 outline-none focus:border-sky-600 transition-colors resize-none"
        />
      </div>
    </div>
  );
};
